import { useState, useEffect, useMemo, useCallback } from "react";
import {
  Mail,
  Plus,
  Search,
  BarChart3,
  Clock,
  CheckCircle,
  AlertCircle,
  X,
  Edit,
  RefreshCw,
} from "lucide-react";
import { toast } from "react-toastify";
import NewsletterTable from "./NewsletterTable";
import AddNewsletterModal from "./AddNewsletterModal";
import DeleteNewsletterModal from "./DeleteNewsletterModal";
import {
  createCampaign,
  deleteCampaign,
  getAllCampaigns,
  updateCampaign,
  sendCampaign,
} from "../../../../api/apiFunction/newsletterServices";
import { getAllIndividualUsers } from "../../../../api/apiFunction/userServices";

const NewsletterTab = () => {
  const [campaigns, setCampaigns] = useState([]);
  console.log("campaigns", campaigns);
  const [loading, setLoading] = useState(true);
  const [sendingCampaignId, setSendingCampaignId] = useState(null); // Track which campaign is being sent
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [intervalActive, setIntervalActive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [audienceFilter, setAudienceFilter] = useState("all");

  // Modal states
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [deletingCampaign, setDeletingCampaign] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  // Define fetchCampaigns function first (before useEffect)
  const fetchCampaigns = useCallback(async (showLoadingState = true) => {
    try {
      console.log(
        "📡 Fetching campaigns... showLoading:",
        showLoadingState,
        "at:",
        new Date().toLocaleTimeString(),
      );
      if (showLoadingState) {
        setLoading(true);
      }
      const response = await getAllCampaigns();
      console.log("📡 API Response received:", response);

      if (response?.data) {
        // Handle different response structures
        let campaignsData = [];

        if (Array.isArray(response.data)) {
          campaignsData = response.data;
        } else if (response.data.campaigns) {
          campaignsData = response.data.campaigns;
        } else if (response.data.data?.campaigns) {
          campaignsData = response.data.data.campaigns;
        }

        // Normalize campaigns to ensure consistent id field
        const normalizedCampaigns = campaignsData.map((campaign) => ({
          ...campaign,
          id: campaign._id || campaign.id, // Ensure we always have an id field
        }));

        console.log("📊 Setting campaigns state:", {
          count: normalizedCampaigns.length,
          campaigns: normalizedCampaigns.map((c) => ({
            id: c.id,
            title: c.title,
            status: c.status,
          })),
        });

        setCampaigns(normalizedCampaigns);
        setLastRefresh(new Date()); // Update last refresh time
        console.log(
          "📊 Campaigns updated:",
          normalizedCampaigns.length,
          "campaigns loaded",
        );
      } else {
        // If no data or error, use empty array
        console.log("⚠️ No data in response, setting empty array");
        setCampaigns([]);
        if (response?.status >= 400) {
          toast.error("Failed to fetch campaigns");
        }
      }
    } catch (error) {
      console.error("❌ Error fetching campaigns:", error);
      setCampaigns([]);
      if (showLoadingState) {
        toast.error("Failed to fetch campaigns");
      }
    } finally {
      if (showLoadingState) {
        setLoading(false);
      }
      console.log(
        "✅ fetchCampaigns completed at:",
        new Date().toLocaleTimeString(),
      );
    }
  }, []); // Empty dependency array since it doesn't depend on any props or state

  // Fetch campaigns on component mount
  useEffect(() => {
    console.log("🚀 Initial fetch on component mount");
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Auto-refresh campaigns every 10 seconds to sync with backend cron job
  useEffect(() => {
    console.log("🔄 Setting up minute+1s aligned auto-refresh...");
    setIntervalActive(true);

    let intervalId;
    let timeoutId;

    const scheduleNext = () => {
      const now = new Date();
      const seconds = now.getSeconds();
      const milliseconds = now.getMilliseconds();

      // ms until next minute (hh:mm:00)
      const msUntilNextMinute = (60 - seconds) * 1000 - milliseconds;

      timeoutId = setTimeout(() => {
        // ⏱ extra 1 second delay
        setTimeout(() => {
          const callTime = new Date();
          console.log(
            "⏰ Refresh at (minute + 1s):",
            callTime.toLocaleTimeString(),
          );

          fetchCampaigns(true);

          // Keep calling every minute at :01
          intervalId = setInterval(() => {
            fetchCampaigns(true);
          }, 60000);
        }, 3000); // ✅ +3 second
      }, msUntilNextMinute);
    };

    scheduleNext();

    return () => {
      console.log("🔄 Clearing minute+1s refresh timers");
      setIntervalActive(false);
      clearTimeout(timeoutId);
      clearInterval(intervalId);
    };
  }, [fetchCampaigns]);

  // Refresh when page becomes visible (user switches back to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log("🔄 Page became visible, refreshing campaigns...");
        fetchCampaigns(false); // Don't show loading state for visibility refresh
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [fetchCampaigns]);

  // Filter campaigns
  const filteredCampaigns = useMemo(() => {
    return campaigns.filter((campaign) => {
      // Safely handle undefined values
      const title = campaign?.title || "";
      const subject = campaign?.subject || "";
      const status = campaign?.status || "";
      const audience = campaign?.audience || "";

      const matchesSearch =
        title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        subject.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === "all" || status === statusFilter;
      const matchesAudience =
        audienceFilter === "all" || audience === audienceFilter;

      return matchesSearch && matchesStatus && matchesAudience;
    });
  }, [campaigns, searchQuery, statusFilter, audienceFilter]);

  // Calculate stats
  const stats = useMemo(() => {
    const total = campaigns.length;
    const sent = campaigns.filter((c) => c.status === "sent").length;
    const scheduled = campaigns.filter((c) => c.status === "scheduled").length;
    const drafts = campaigns.filter((c) => c.status === "draft").length;
    const avgOpenRate =
      campaigns
        .filter((c) => c.openRate)
        .reduce((acc, c) => acc + c.openRate, 0) /
        campaigns.filter((c) => c.openRate).length || 0;

    return { total, sent, scheduled, drafts, avgOpenRate };
  }, [campaigns]);

  const handleAddCampaign = () => {
    setEditingCampaign(null);
    setAddModalOpen(true);
  };

  const handleEditCampaign = (campaign) => {
    setEditingCampaign(campaign);
    setAddModalOpen(true);
  };

  const handleDeleteCampaign = (campaign) => {
    setDeletingCampaign(campaign);
    setDeleteModalOpen(true);
  };

  const handleSaveCampaign = async (campaignData) => {
    try {
      if (editingCampaign) {
        // Update existing campaign - use _id or id
        const campaignId = editingCampaign._id || editingCampaign.id;
        const response = await updateCampaign(campaignId, campaignData);

        if (response?.data) {
          toast.success("Campaign updated successfully");
        } else {
          toast.error("Failed to update campaign");
          return;
        }
      } else {
        // Create new campaign
        const response = await createCampaign(campaignData);

        if (response?.data) {
          toast.success("Campaign created successfully");
        } else {
          toast.error("Failed to create campaign");
          return;
        }
      }

      // Close modal first
      setAddModalOpen(false);
      setEditingCampaign(null);

      // Fetch fresh data from API
      await fetchCampaigns();
    } catch (error) {
      console.error("Error saving campaign:", error);
      toast.error(
        editingCampaign
          ? "Failed to update campaign"
          : "Failed to create campaign",
      );
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingCampaign) return;

    try {
      // Use _id or id - handle both MongoDB and other DB formats
      const campaignId = deletingCampaign._id || deletingCampaign.id;

      if (!campaignId) {
        toast.error("Invalid campaign ID");
        return;
      }

      const response = await deleteCampaign(campaignId);

      if (response?.status === 200) {
        toast.success("Campaign deleted successfully");

        // Close modal first
        setDeleteModalOpen(false);
        setDeletingCampaign(null);

        // Fetch fresh data from API
        await fetchCampaigns();
      } else {
        toast.error("Failed to delete campaign");
      }
    } catch (error) {
      console.error("Error deleting campaign:", error);
      toast.error("Failed to delete campaign");
    }
  };

  const handleSendCampaign = async (campaign) => {
    try {
      console.log("🔍 Campaign object received:", campaign);
      console.log("🔍 Campaign selectedUsers:", campaign.selectedUsers);

      const campaignId = campaign._id || campaign.id;

      if (!campaignId) {
        toast.error("Invalid campaign ID");
        return;
      }

      // Set loading state for this specific campaign
      setSendingCampaignId(campaignId);

      // Show loading toast
      // toast.info('Sending campaign...');

      // Fetch all users to get emails for selectedUsers IDs
      let selectedUserEmails = [];

      if (
        campaign.selectedUsers &&
        Array.isArray(campaign.selectedUsers) &&
        campaign.selectedUsers.length > 0
      ) {
        try {
          const usersResponse = await getAllIndividualUsers();

          if (
            usersResponse?.data?.users &&
            Array.isArray(usersResponse.data.users)
          ) {
            const allUsers = usersResponse.data.users;

            // Convert user IDs to emails
            selectedUserEmails = campaign.selectedUsers
              .map((userId) => {
                // Handle both string IDs and user objects
                const id = typeof userId === "string" ? userId : userId._id;
                const user = allUsers.find((u) => u._id === id);
                return user ? user.email : null;
              })
              .filter((email) => email !== null); // Remove null values

            console.log("📧 Converted user IDs to emails:", {
              userIds: campaign.selectedUsers.length,
              emails: selectedUserEmails.length,
              emailList: selectedUserEmails,
            });
          }
        } catch (userError) {
          console.error(
            "Error fetching users for email conversion:",
            userError,
          );
          toast.error("Failed to fetch user emails");
          setSendingCampaignId(null); // Clear loading state
          return;
        }
      } else {
        console.log(
          "⚠️ No selectedUsers found, fetching all users based on audience type",
        );

        // If no selectedUsers, get all users based on audience type
        try {
          const usersResponse = await getAllIndividualUsers();

          if (
            usersResponse?.data?.users &&
            Array.isArray(usersResponse.data.users)
          ) {
            const allUsers = usersResponse.data.users;

            // Filter users based on audience type
            let filteredUsers = [];
            switch (campaign.audience) {
              case "all_users":
                filteredUsers = allUsers;
                break;
              case "active_subscribers":
                filteredUsers = allUsers.filter(
                  (user) => user.plan && user.plan.name !== "free",
                );
                break;
              case "trial_users":
                filteredUsers = allUsers.filter(
                  (user) => user.plan && user.plan.name === "trial",
                );
                break;
              case "non_billing_users":
                filteredUsers = allUsers.filter(
                  (user) => !user.plan || user.plan.name === "free",
                );
                break;
              default:
                filteredUsers = allUsers;
            }

            selectedUserEmails = filteredUsers.map((user) => user.email);

            console.log("📧 Fallback: Using audience-based emails:", {
              audience: campaign.audience,
              totalUsers: allUsers.length,
              filteredUsers: filteredUsers.length,
              emails: selectedUserEmails.length,
            });
          }
        } catch (userError) {
          console.error("Error fetching users for fallback:", userError);
          toast.error("Failed to fetch user emails");
          setSendingCampaignId(null); // Clear loading state
          return;
        }
      }

      const payload = {
        selectedUsers: selectedUserEmails,
      };

      console.log("📤 Final payload being sent:", payload);

      const response = await sendCampaign(campaignId, payload);

      if (response?.status === 200) {
        toast.success(
          `Campaign sent successfully to ${selectedUserEmails.length} recipients!`,
        );

        // Refresh campaigns to show updated status
        await fetchCampaigns();
      } else {
        toast.error("Failed to send campaign");
      }
    } catch (error) {
      console.error("Error sending campaign:", error);
      toast.error("Failed to send campaign");
    } finally {
      // Always clear loading state
      setSendingCampaignId(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "sent":
        return <CheckCircle size={16} className="text-green-500" />;
      case "scheduled":
        return <Clock size={16} className="text-blue-500" />;
      case "draft":
        return <Edit size={16} className="text-gray-500" />;
      default:
        return <AlertCircle size={16} className="text-red-500" />;
    }
  };

  const getAudienceLabel = (audience) => {
    const labels = {
      all_users: "All Users",
      active_subscribers: "Active Subscribers",
      trial_users: "Trial Users",
      non_billing_users: "Non-billing Users",
      custom: "Custom Segment",
    };
    return labels[audience] || audience;
  };

  return (
    <div className="h-full p-6 custom-scrollbar overflow-y-auto">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          {loading ? (
            <>
              <div className="flex-1">
                <div className="h-8 w-80 bg-bg-40 rounded animate-pulse mb-2"></div>
                <div className="h-4 w-96 bg-bg-40 rounded animate-pulse"></div>
              </div>
              <div className="flex items-center gap-3">
                <div className="h-10 w-24 bg-bg-40 rounded-lg animate-pulse"></div>
                <div className="h-10 w-36 bg-bg-40 rounded-lg animate-pulse"></div>
              </div>
            </>
          ) : (
            <>
              <div>
                <h1 className="text-2xl font-semibold text-fg-50 flex items-center gap-2">
                  <Mail size={24} />
                  Newsletter & Email Campaigns
                </h1>
                <p className="text-sm text-fg-60 mt-1">
                  Create and manage email campaigns, newsletters, and announcements
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    console.log("🔄 Manual refresh triggered");
                    fetchCampaigns();
                  }}
                  className="flex items-center gap-2 px-3 py-2 border border-bd-50 text-fg-50 rounded-lg hover:bg-bg-50 transition-colors"
                  title="Refresh campaigns"
                >
                  <RefreshCw size={16} />
                  Refresh
                </button>
                <button
                  onClick={handleAddCampaign}
                  className="flex items-center gap-2 px-4 py-2 bg-ac-02 text-white rounded-lg hover:bg-ac-01 transition-colors"
                >
                  <Plus size={18} />
                  New Campaign
                </button>
              </div>
            </>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          {loading ? (
            // Loading skeleton for stats cards
            [...Array(5)].map((_, index) => (
              <div
                key={index}
                className="bg-bg-60 rounded-lg p-4 border border-bd-50 animate-pulse"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="h-3 w-20 bg-bg-40 rounded mb-2"></div>
                    <div className="h-6 w-8 bg-bg-40 rounded"></div>
                  </div>
                  <div className="h-5 w-5 bg-bg-40 rounded"></div>
                </div>
              </div>
            ))
          ) : (
            <>
              <div className="bg-bg-60 rounded-lg p-4 border border-bd-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-fg-60 uppercase tracking-wide">
                      Total Campaigns
                    </p>
                    <p className="text-2xl font-semibold text-fg-50 mt-1">
                      {stats.total}
                    </p>
                  </div>
                  <Mail className="text-fg-60" size={20} />
                </div>
              </div>

              <div className="bg-bg-60 rounded-lg p-4 border border-bd-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-fg-60 uppercase tracking-wide">
                      Sent
                    </p>
                    <p className="text-2xl font-semibold text-green-500 mt-1">
                      {stats.sent}
                    </p>
                  </div>
                  <CheckCircle className="text-green-500" size={20} />
                </div>
              </div>

              <div className="bg-bg-60 rounded-lg p-4 border border-bd-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-fg-60 uppercase tracking-wide">
                      Scheduled
                    </p>
                    <p className="text-2xl font-semibold text-blue-500 mt-1">
                      {stats.scheduled}
                    </p>
                  </div>
                  <Clock className="text-blue-500" size={20} />
                </div>
              </div>

              <div className="bg-bg-60 rounded-lg p-4 border border-bd-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-fg-60 uppercase tracking-wide">
                      Drafts
                    </p>
                    <p className="text-2xl font-semibold text-gray-500 mt-1">
                      {stats.drafts}
                    </p>
                  </div>
                  <Edit className="text-gray-500" size={20} />
                </div>
              </div>

              <div className="bg-bg-60 rounded-lg p-4 border border-bd-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-fg-60 uppercase tracking-wide">
                      Avg Open Rate
                    </p>
                    <p className="text-2xl font-semibold text-fg-50 mt-1">
                      {stats.avgOpenRate.toFixed(1)}%
                    </p>
                  </div>
                  <BarChart3 className="text-fg-60" size={20} />
                </div>
              </div>
            </>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          {loading ? (
            // Loading skeleton for filters
            <>
              <div className="flex-1 h-10 bg-bg-40 rounded-lg animate-pulse"></div>
              <div className="h-10 w-32 bg-bg-40 rounded-lg animate-pulse"></div>
              <div className="h-10 w-40 bg-bg-40 rounded-lg animate-pulse"></div>
            </>
          ) : (
            <>
              {/* Search */}
              <div className="flex-1 relative">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-fg-60"
                />
                <input
                  type="text"
                  placeholder="Search campaigns..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 text-sm border border-bd-50 rounded-lg bg-bg-60 text-fg-50 placeholder-fg-60 focus:outline-none focus:ring-2 focus:ring-ac-02 focus:border-transparent"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-fg-60 hover:text-fg-50"
                  >
                    <X size={16} />
                  </button>
                )}
              </div>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-bd-50 rounded-lg bg-bg-60 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="draft">Draft</option>
                <option value="scheduled">Scheduled</option>
                <option value="sent">Sent</option>
              </select>

              {/* Audience Filter */}
              <select
                value={audienceFilter}
                onChange={(e) => setAudienceFilter(e.target.value)}
                className="px-3 py-2 text-sm border border-bd-50 rounded-lg bg-bg-60 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 focus:border-transparent"
              >
                <option value="all">All Audiences</option>
                <option value="all_users">All Users</option>
                <option value="active_subscribers">Active Subscribers</option>
                <option value="trial_users">Trial Users</option>
                <option value="non_billing_users">Non-billing Users</option>
                <option value="custom">Custom Segment</option>
              </select>
            </>
          )}
        </div>

        {/* Campaigns Table */}
        <div className="bg-bg-60 rounded-lg border border-bd-50 overflow-hidden">
          <NewsletterTable
            campaigns={filteredCampaigns}
            loading={loading}
            sendingCampaignId={sendingCampaignId}
            onEdit={handleEditCampaign}
            onDelete={handleDeleteCampaign}
            onSend={handleSendCampaign}
            getStatusIcon={getStatusIcon}
            getAudienceLabel={getAudienceLabel}
          />
        </div>

        {/* Modals */}
        <AddNewsletterModal
          isOpen={addModalOpen}
          onClose={() => {
            setAddModalOpen(false);
            setEditingCampaign(null);
          }}
          onSave={handleSaveCampaign}
          campaign={editingCampaign}
          isEditing={!!editingCampaign}
        />

        <DeleteNewsletterModal
          isOpen={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setDeletingCampaign(null);
          }}
          onConfirm={handleConfirmDelete}
          campaign={deletingCampaign}
        />
      </div>
    </div>
  );
};

export default NewsletterTab;
